import { Action } from 'store/store';
import { WidgetType } from 'widgets/WidgetType';
import { WidgetTypeActionPayload, WidgetActionType } from '../actions';
import { AnyAction } from 'redux';

export interface WidgetSetConfigPayload<T> extends WidgetTypeActionPayload {
    configDelta: Partial<T>;
    replace?: boolean;
}

export type WidgetSetConfigAction<T> = Action<
    WidgetActionType.WIDGET_SET_CONFIG,
    WidgetSetConfigPayload<T>
>;

export function createUpdateWidgetConfigurationAction<T>(
    widgetType: WidgetType,
    widgetKey: string,
    configDelta: Partial<T>,
    replace?: boolean
): WidgetSetConfigAction<T> {
    if (!widgetType || !widgetKey) {
        throw new Error('No widget type or widget key received');
    }
    return {
        type: WidgetActionType.WIDGET_SET_CONFIG,
        payload: { widgetType, widgetKey, configDelta, replace },
    };
}

export function buildUpdateWidgetConfigurationActionCreator<T>(
    widgetType: WidgetType
) {
    return (
        widgetKey: string,
        configDelta: Partial<T>,
        replace?: boolean
    ): WidgetSetConfigAction<T> =>
        createUpdateWidgetConfigurationAction(
            widgetType,
            widgetKey,
            configDelta,
            replace
        );
}

export function isSetWidgetConfigAction(
    action: AnyAction
): action is WidgetSetConfigAction<unknown> {
    return action.type === WidgetActionType.WIDGET_SET_CONFIG;
}

export type WidgetLoadConfigAction<T> = Action<
    WidgetActionType.WIDGET_LOAD_CONFIG,
    WidgetSetConfigPayload<T>
>;

export function createLoadWidgetConfigurationAction<T>(widgetType: WidgetType) {
    return (
        widgetKey: string,
        configDelta: Partial<T>
    ): WidgetLoadConfigAction<T> => ({
        type: WidgetActionType.WIDGET_LOAD_CONFIG,
        payload: { widgetType, widgetKey, configDelta },
    });
}

export function isLoadWidgetConfigAction(
    action: AnyAction
): action is WidgetLoadConfigAction<unknown> {
    return action.type === WidgetActionType.WIDGET_LOAD_CONFIG;
}

export type WidgetConfigAction<T> =
    | WidgetSetConfigAction<T>
    | WidgetLoadConfigAction<T>;
